import { createSignal } from "solid-js";

import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { MainCategoryTypes } from "@/components/PC/Modals/GlobalSearch/constants";
import { GameSearchSort } from "@/enum/component";
import { queryGameParamsType } from "@/hooks/useCasino";
import { getLangKey } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { sysConfigStore } from "@/stores/sysConfigStore";
import { SearchSportsDataReq, SportEventInfo } from "@/types/swagger/extGame";
import { GameNode } from "@/types/swagger/front";

const DEFAULT_CASINO_PAGE_SIZE = 42;
const DEFAULT_SPORTS_PAGE_SIZE = 10;

// 搜索状态接口定义
interface GlobalSearchType {
  // 模态框状态
  isSearchModalOpen: boolean;

  // 主分类相关
  curMainCategory: MainCategoryTypes;
  curSubCategoryIdx: number;
  categoryIsOpen: boolean;

  // 搜索框相关
  isPopupVisible: boolean;
  searchValue: string;

  // API 参数
  searchCasinoParams: queryGameParamsType & { name: string };
  searchSportsParams: SearchSportsDataReq;

  // 数据相关
  gameList: (GameNode | SportEventInfo)[];
  gameListCount: number;
  isSearching: boolean;
  isLoadFinished: boolean;
  apiLoading: boolean; // 是否正在加载
  isInit: boolean;

  // 历史记录和推荐
  searchHistory: string[];
  searchRecommend: string[];
}

// 默认 Casino 参数
const DEFAULT_CASINO_PARAMS: queryGameParamsType & { name: string } = {
  name: "",
  maker_tag: "",
  page: 1,
  page_size: DEFAULT_CASINO_PAGE_SIZE,
  sort: GameSearchSort.Popular,
  tag: "",
  gclass: "", // 子分类
};

// 默认 Sports 参数
const getDefaultSportsParams = (): SearchSportsDataReq => ({
  from_type: sysConfigStore?.sport_config?.sport_priority_type, // 依照 sysConfig 配置的内容去选择 betby 还是 first
  lang: getLangKey(),
  page: 1,
  page_size: DEFAULT_SPORTS_PAGE_SIZE,
  query: "",
  sport_id: 0,
});

export const [globalSearch, setGlobalSearch] = createSignal<GlobalSearchType>({
  // 模态框状态
  isSearchModalOpen: false,

  // 主分类相关
  curMainCategory: "Casino",
  curSubCategoryIdx: 0,
  categoryIsOpen: false,

  // 搜索框相关
  isPopupVisible: false,
  searchValue: "",

  // API 参数
  searchCasinoParams: { ...DEFAULT_CASINO_PARAMS },
  searchSportsParams: { ...getDefaultSportsParams() },

  // 数据相关
  gameList: [],
  gameListCount: 0,
  isSearching: false,
  isLoadFinished: false,
  apiLoading: false, // 是否正在加载
  isInit: false,

  // 历史记录和推荐
  searchHistory: [],
  searchRecommend: [],
});

export const useGlobalSearch = () => {
  const { showDialog, closeDialog } = useDialog();

  const openGlobalSearchModal = () => {
    if (isMobile()) {
      setGlobalSearch((prev) => ({ ...prev, isSearchModalOpen: true }));
    } else {
      showDialog({ type: DialogType.Search });
    }
  };

  const closeGlobalSearchModal = () => {
    if (isMobile()) {
      setGlobalSearch((prev) => ({ ...prev, isSearchModalOpen: false }));
    } else {
      closeDialog(DialogType.Search);
    }
    // 重置状态
    resetGlobalSearchModal();
  };

  const resetGlobalSearchModal = () => {
    // 关闭模态框时清空搜索值和相关状态
    setGlobalSearch((prev) => ({ ...prev, searchValue: "" }));
    setGlobalSearch((prev) => ({ ...prev, isPopupVisible: false }));
    setGlobalSearch((prev) => ({ ...prev, categoryIsOpen: false }));
    // 重置搜索参数
    /* prettier-ignore */
    setGlobalSearch((prev) => ({...prev,searchCasinoParams: { ...DEFAULT_CASINO_PARAMS }}));
    /* prettier-ignore */
    setGlobalSearch((prev) => ({...prev,searchSportsParams: { ...getDefaultSportsParams() }}));
  };

  // ========== 主分类操作方法 ==========
  const setCurMainCategory = (category: MainCategoryTypes) => {
    setGlobalSearch((prev) => ({ ...prev, curMainCategory: category }));
  };

  const setCurSubCategoryIdx = (idx: number) => {
    setGlobalSearch((prev) => ({ ...prev, curSubCategoryIdx: idx }));
  };

  const setCategoryIsOpen = (isOpen: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, categoryIsOpen: isOpen }));
  };

  // ========== 搜索框操作方法 ==========
  const setPopupVisible = (visible: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, isPopupVisible: visible }));
  };

  const setSearchValue = (value: string) => {
    setGlobalSearch((prev) => ({ ...prev, searchValue: value }));
  };

  // ========== API 参数操作方法 ==========
  const setSearchCasinoParams = (
    params: Partial<queryGameParamsType & { name: string }>,
  ) => {
    /* prettier-ignore */
    setGlobalSearch((prev) => ({ ...prev, searchCasinoParams: { ...prev.searchCasinoParams, ...params } }));
  };

  const setSearchSportsParams = (params: Partial<SearchSportsDataReq>) => {
    /* prettier-ignore */
    setGlobalSearch((prev) => ({ ...prev, searchSportsParams: { ...prev.searchSportsParams, ...params } }));
  };

  // ========== 数据操作方法 ==========
  // 设置游戏列表
  const setGameList = (list: (GameNode | SportEventInfo)[]) => {
    setGlobalSearch((prev) => ({ ...prev, gameList: list }));
  };

  // 追加游戏列表
  const appendGameList = (list: (GameNode | SportEventInfo)[]) => {
    /* prettier-ignore */
    setGlobalSearch((prev) => ({ ...prev, gameList: [...prev.gameList, ...list] }));
  };

  // 设置游戏列表总数
  const setGameListCount = (count: number) => {
    setGlobalSearch((prev) => ({ ...prev, gameListCount: count }));
  };

  // 设置是否正在搜索
  const setIsSearching = (searching: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, isSearching: searching }));
  };

  // 设置是否加载完成
  const setIsLoadFinished = (finished: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, isLoadFinished: finished }));
  };

  // 设置是否请求完毕
  const setApiLoading = (apiLoading: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, apiLoading: apiLoading }));
  };

  // 设置是否初始化
  const setIsInit = (init: boolean) => {
    setGlobalSearch((prev) => ({ ...prev, isInit: init }));
  };

  // ========== 历史记录和推荐操作方法 ==========
  const setSearchHistory = (history: string[]) => {
    setGlobalSearch((prev) => ({ ...prev, searchHistory: history }));
  };

  const setSearchRecommend = (recommend: string[]) => {
    setGlobalSearch((prev) => ({ ...prev, searchRecommend: recommend }));
  };

  const resetGlobalSearch = () => {
    setGlobalSearch({
      isSearchModalOpen: false,
      curMainCategory: "Casino",
      curSubCategoryIdx: 0,
      categoryIsOpen: false,
      isPopupVisible: false,
      searchValue: "",
      searchCasinoParams: { ...DEFAULT_CASINO_PARAMS },
      searchSportsParams: { ...getDefaultSportsParams() },
      gameList: [],
      gameListCount: 0,
      isSearching: false,
      isLoadFinished: false,
      apiLoading: false, // 是否正在加载
      isInit: false,
      searchHistory: [],
      searchRecommend: [],
    });
  };

  return {
    // // 搜索
    // globalSearch,
    // setGlobalSearch,

    // 开启 modal
    openGlobalSearchModal,
    // 关闭 modal
    closeGlobalSearchModal,
    // 重置
    resetGlobalSearchModal,

    // 主分类
    setCurMainCategory,
    setCurSubCategoryIdx,
    setCategoryIsOpen,

    // 搜索框
    setPopupVisible,
    setSearchValue,

    // API 参数
    setSearchCasinoParams,
    setSearchSportsParams,

    // 数据
    setGameList,
    appendGameList,
    setGameListCount,
    setIsSearching,
    setIsLoadFinished,
    setApiLoading,
    setIsInit,

    // 历史记录和推荐
    setSearchHistory,
    setSearchRecommend,

    // 全部重置
    resetGlobalSearch,
  };
};
